/**
 * Sustainability-indicator sign-off UI (PLT-5088, iteration item 2).
 *
 * The indicator values the agent extracted from a user's uploaded report render as one editable
 * block:
 *
 *   - each row names the indicator (resolved from `SUSTAINABILITY_INDICATOR_METADATA`) and its value,
 *     edited as a percentage (0–100) or a whole count depending on the indicator's value type;
 *   - validation computed client-side with the SAME canonical rule the backend enforces at commit
 *     (`validateSustainabilityIndicatorValue`);
 *   - a Save (with an inline are-you-sure step) that POSTs the signed-off set directly to
 *     `POST /api/company/{companyId}/sustainability-indicators/batch` — NOT back through the LLM.
 *
 * The block appears in two places: inside `<OpDataBatchCard>` when the same file also had
 * operational-data splits (one upload, one card — its "Save all" covers the block too), and on its
 * own in `<SustainabilityIndicatorsBatchCard>` for `propose_sustainability_indicators_batch`. Both
 * share {@link useSustainabilityIndicatorsApply} for state and the write.
 *
 * Applying MERGES the proposed indicators into the company's current-year set (untouched indicators
 * are left in place), so a partial upload is safe. The values are saved for the current year whatever
 * year the report covers; the report year only labels the block. After a successful apply the block
 * collapses into an immutable receipt. The agent never writes.
 *
 * Reuses the `op-data-batch-*` styling for visual consistency with the operational-data card.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DisclosureDataPointId,
  SUSTAINABILITY_INDICATOR_METADATA,
} from '@upright/common/model/input/companies/sustainability-indicators/types';
import { ApplySustainabilityIndicatorsBatchResult } from '@upright/common/model/input/companies/sustainability-indicators/api-types';
import { validateSustainabilityIndicatorValue } from '@upright/common/model/input/companies/sustainability-indicators/validation';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { HAS_UNINTEGRATED_CHANGES_QUERY_KEY } from '@upright/ui/common/generic/components/operational-data-input/query-keys';
import { ChatMarkdown } from './upright-chat-markdown';
import { Badge } from './components';
import { savedForYearLabel } from './chat-upload-year';

export interface ProposedIndicator {
  disclosureId: DisclosureDataPointId;
  value: number;
  note?: string;
}

export interface SustainabilityIndicatorsBatchCardProps {
  companyId: string;
  companyName?: string;
  proposalId: string;
  reportYear?: number;
  summaryMarkdown?: string;
  indicators: ProposedIndicator[];
}

type BlockState =
  | { phase: 'idle' }
  | { phase: 'confirming' }
  | { phase: 'applying' }
  | { phase: 'applied'; result: ApplySustainabilityIndicatorsBatchResult }
  | { phase: 'failed'; message: string };

// A row's current value as a raw input string (so the user can clear / type freely). Absent → the
// agent's proposed value. Empty string → treated as NaN (an error, since every indicator needs a value).
type ValueEdits = Record<number, string | undefined>;

function effectiveValue(indicators: ProposedIndicator[], edits: ValueEdits, idx: number): number {
  const raw = edits[idx];
  if (raw === undefined) return indicators[idx].value;
  const trimmed = raw.trim();
  if (trimmed === '') return NaN;
  return Number(trimmed);
}

function rowInputValue(indicators: ProposedIndicator[], edits: ValueEdits, idx: number): string {
  const raw = edits[idx];
  if (raw !== undefined) return raw;
  return String(indicators[idx].value);
}

export interface SustainabilityIndicatorsApply {
  indicators: ProposedIndicator[];
  reportYear?: number;
  edits: ValueEdits;
  editValue: (idx: number, value: string) => void;
  rowErrors: string[][];
  canSave: boolean;
  state: BlockState;
  setState: (next: BlockState) => void;
  apply: () => Promise<void>;
}

/** Edit state, validation and the write for one block of proposed indicators. */
export function useSustainabilityIndicatorsApply(
  companyId: string,
  indicators: ProposedIndicator[],
  reportYear?: number,
): SustainabilityIndicatorsApply {
  const [edits, setEdits] = useState<ValueEdits>({});
  const [state, setState] = useState<BlockState>({ phase: 'idle' });
  const queryClient = useQueryClient();

  const rowErrors = indicators.map((indicator, idx) =>
    validateSustainabilityIndicatorValue(indicator.disclosureId, effectiveValue(indicators, edits, idx)),
  );
  const canSave = indicators.length > 0 && rowErrors.every(errs => errs.length === 0);

  const editValue = (idx: number, value: string) => {
    setEdits(prev => ({ ...prev, [idx]: value }));
  };

  const apply = async (): Promise<void> => {
    if (!canSave) return;
    setState({ phase: 'applying' });
    try {
      const result = await fetchOrThrow<ApplySustainabilityIndicatorsBatchResult>(
        `/company/${encodeURIComponent(companyId)}/sustainability-indicators/batch`,
        'POST',
        {
          reportYear,
          indicators: indicators.map((indicator, idx) => ({
            disclosureId: indicator.disclosureId,
            value: effectiveValue(indicators, edits, idx),
          })),
        },
      );
      setState({ phase: 'applied', result });
      // As for operational data: the indicators form may be open next to the chat and does not poll,
      // so refresh it and the "Rerun assessment" eligibility the way `useSaveSustainabilityIndicators`
      // does. Keyed by the company the endpoint resolved, which is the ID the form queries by.
      void queryClient.invalidateQueries({ queryKey: ['sustainabilityIndicators', result.companyId] });
      void queryClient.invalidateQueries({ queryKey: [HAS_UNINTEGRATED_CHANGES_QUERY_KEY] });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save these indicators.';
      setState({ phase: 'failed', message });
    }
  };

  return { indicators, reportYear, edits, editValue, rowErrors, canSave, state, setState, apply };
}

/**
 * One block of proposed indicators, styled as a dataset block of the operational-data card. Shows
 * the report/save year in its title only when `showYear` is set — inside the combined card the card
 * subtitle already carries it.
 */
export function SustainabilityIndicatorsBlock({
  block,
  showYear,
}: {
  block: SustainabilityIndicatorsApply;
  showYear: boolean;
}) {
  const { indicators, reportYear, edits, editValue, rowErrors, canSave, state, setState, apply } = block;
  const title = showYear
    ? `Sustainability indicators · ${savedForYearLabel([reportYear])}`
    : 'Sustainability indicators';

  if (state.phase === 'applied') {
    return (
      <section
        className="op-data-batch-dataset op-data-batch-dataset--receipt"
        aria-label="Saved sustainability indicators"
      >
        <header className="op-data-batch-dataset-header">
          <div className="op-data-batch-dataset-title">{title}</div>
          {/* Neutral: "saved" is an operation outcome, not positive impact. */}
          <Badge tone="neutral">Saved</Badge>
        </header>
        <div className="op-data-batch-dataset-receipt-detail">
          {state.result.rowCount} {state.result.rowCount === 1 ? 'indicator' : 'indicators'} saved for{' '}
          {state.result.year}
        </div>
      </section>
    );
  }

  const locked = state.phase === 'confirming' || state.phase === 'applying';

  return (
    <section className="op-data-batch-dataset" aria-label="Proposed sustainability indicators">
      <header className="op-data-batch-dataset-header">
        <div className="op-data-batch-dataset-title">{title}</div>
      </header>
      <ul className="op-data-batch-dataset-rows">
        {indicators.map((indicator, idx) => {
          const meta = SUSTAINABILITY_INDICATOR_METADATA[indicator.disclosureId];
          const isPercentage = meta.valueType === 'percentage';
          return (
            <li key={indicator.disclosureId} className="op-data-batch-row">
              <span className="op-data-batch-row-key">
                {meta.name}
                {indicator.note && <span className="op-data-batch-dataset-note"> — {indicator.note}</span>}
              </span>
              <span className="op-data-batch-row-input-wrap">
                <input
                  type="number"
                  min={0}
                  max={isPercentage ? 100 : undefined}
                  step={isPercentage ? 0.1 : 1}
                  className="op-data-batch-row-input"
                  value={rowInputValue(indicators, edits, idx)}
                  disabled={locked}
                  aria-label={`Value for ${meta.name}`}
                  onChange={e => {
                    editValue(idx, e.target.value);
                  }}
                />
                <span className="op-data-batch-row-pct" aria-hidden="true">
                  {isPercentage ? '%' : '#'}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {!canSave && (
        <div className="op-data-batch-dataset-errors" role="alert">
          {rowErrors.flatMap((errs, idx) =>
            errs.map((msg, i) => (
              <span key={`${idx}-${i}`}>
                {`${SUSTAINABILITY_INDICATOR_METADATA[indicators[idx].disclosureId].name}: ${msg}`}
              </span>
            )),
          )}
        </div>
      )}
      {state.phase === 'confirming' ? (
        <div className="op-data-batch-dataset-confirm" role="group" aria-label="Confirm save">
          <div className="op-data-batch-dataset-confirm-prompt">
            Save these indicators for {new Date().getFullYear()}? They merge into the company&apos;s sustainability
            data; indicators not listed here stay as they are.
          </div>
          {/* Secondary first, primary last — the one control order the product uses everywhere. */}
          <div className="op-data-batch-dataset-action-row">
            <button
              type="button"
              className="op-data-batch-card-action op-data-batch-card-action--secondary"
              onClick={() => {
                setState({ phase: 'idle' });
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="op-data-batch-card-action op-data-batch-card-action--primary"
              onClick={() => {
                void apply();
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      ) : (
        <div className="op-data-batch-dataset-action-row">
          <button
            type="button"
            className="op-data-batch-card-action op-data-batch-card-action--secondary"
            disabled={!canSave || state.phase === 'applying'}
            onClick={() => {
              setState({ phase: 'confirming' });
            }}
          >
            {state.phase === 'applying' ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
      {state.phase === 'failed' && (
        <div className="op-data-batch-dataset-error" role="alert">
          {state.message}
        </div>
      )}
    </section>
  );
}

/** Stand-alone card for `propose_sustainability_indicators_batch` (a report with no operational data). */
export function SustainabilityIndicatorsBatchCard({
  companyId,
  companyName,
  reportYear,
  summaryMarkdown,
  indicators,
}: SustainabilityIndicatorsBatchCardProps) {
  const block = useSustainabilityIndicatorsApply(companyId, indicators, reportYear);

  return (
    <section className="op-data-batch-card" aria-label="Proposed sustainability indicators">
      <header className="op-data-batch-card-header">
        <div className="op-data-batch-card-title">Proposed sustainability indicators</div>
        <div className="op-data-batch-card-subtitle">
          {`${indicators.length} ${indicators.length === 1 ? 'indicator' : 'indicators'} · ${savedForYearLabel([reportYear])}`}
          {companyName ? ` · ${companyName}` : ''}
        </div>
      </header>
      {summaryMarkdown && summaryMarkdown.length > 0 && (
        <div className="op-data-batch-card-summary">
          <ChatMarkdown>{summaryMarkdown}</ChatMarkdown>
        </div>
      )}
      <div className="op-data-batch-card-datasets">
        <SustainabilityIndicatorsBlock block={block} showYear={false} />
      </div>
    </section>
  );
}
